import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { listFolders, setMeetingFolder, setMeetingTags } from "@/lib/folders";
import type { Folder } from "@/types";

interface Props {
  meetingId: string;
  folderId: string | null;
  tags: string[];
  onChange?: () => void;
}

export function MeetingTagsEditor({ meetingId, folderId, tags, onChange }: Props) {
  const { t } = useTranslation();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [tagsState, setTagsState] = useState<string[]>(tags);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listFolders().then(setFolders).catch(() => {});
  }, []);

  useEffect(() => {
    setTagsState(tags);
  }, [tags]);

  const persistTags = async (next: string[]) => {
    try {
      await setMeetingTags(meetingId, next);
      onChange?.();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const addTag = () => {
    const tag = draft.trim().toLowerCase();
    if (!tag || tagsState.includes(tag)) {
      setDraft("");
      return;
    }
    const next = [...tagsState, tag];
    setTagsState(next);
    setDraft("");
    persistTags(next);
  };

  const removeTag = (tag: string) => {
    const next = tagsState.filter((x) => x !== tag);
    setTagsState(next);
    persistTags(next);
  };

  const pickFolder = async (id: string | null) => {
    try {
      await setMeetingFolder(meetingId, id);
      onChange?.();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="bg-redi-surface/50 rounded-xl p-3 mb-4">
      <p className="text-redi-muted text-xs uppercase tracking-wide mb-2">
        {t("folders.label")}
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        <button
          onClick={() => pickFolder(null)}
          className={`px-3 py-1 rounded-full text-xs ${
            folderId === null
              ? "bg-redi-primary text-white"
              : "bg-redi-dark text-redi-muted hover:text-redi-text"
          }`}
        >
          {t("folders.none")}
        </button>
        {folders.map((f) => (
          <button
            key={f.id}
            onClick={() => pickFolder(f.id)}
            className={`px-3 py-1 rounded-full text-xs ${
              folderId === f.id
                ? "bg-redi-primary text-white"
                : "bg-redi-dark text-redi-muted hover:text-redi-text"
            }`}
          >
            {f.name}
          </button>
        ))}
      </div>

      <p className="text-redi-muted text-xs uppercase tracking-wide mb-2">
        {t("tags.label")}
      </p>
      <div className="flex flex-wrap gap-2 mb-2">
        {tagsState.map((tag) => (
          <button
            key={tag}
            onClick={() => removeTag(tag)}
            className="bg-redi-primary/20 text-redi-primary text-xs px-3 py-1 rounded-full hover:bg-redi-primary/30"
          >
            #{tag} <span className="ml-1 opacity-60">×</span>
          </button>
        ))}
      </div>
      <div className="flex items-center bg-redi-dark rounded-lg px-3">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder={t("tags.placeholder")}
          maxLength={32}
          className="flex-1 bg-transparent text-redi-text text-xs py-2 outline-none"
        />
        {draft.trim().length > 0 && (
          <button
            onClick={addTag}
            className="text-redi-primary text-sm px-2"
          >
            +
          </button>
        )}
      </div>
      {error && <p className="text-redi-error text-xs mt-2">{error}</p>}
    </div>
  );
}
